import {Component, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {ToastrService} from 'ngx-toastr';
import {Subscription} from 'rxjs';
import dayjs from 'dayjs';
import {OverlaySpinnerComponent} from '@shared/components/overlay-spinner/overlay-spinner.component';
import {PatientStatisticsService} from '@shared/api/services/v2/patient-statistics.service';
import {
    CarerWeeklyAdherence,
    CarerWeeklySnapshotV2,
    WeeklyDailyCheckinsRollupV2,
    WeeklyHowItsGoingV2,
    WeeklyMealPlanLegacy,
    WeeklyMealPlanV2,
    WeeklyObservationsV2,
    WeeklySnapshot_Chapter,
    isWeeklySnapshotV2
} from '@shared/api/models/query/patient-weekly-snapshot.model';
import {DailyBehaviourV2} from '@shared/api/models/query/patient-daily-snapshot.model';
import {
    WeeklyHowItsGoingAnswerLabels,
    WeeklyObservationLabels,
    resolveWeeklyCarerCopy
} from '@cdss/pages/patient-overview-v2/detail/weekly/weekly-carer-copy';
import {WeeklyAdherenceDonutsComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-adherence-donuts.component';
import {WeeklyDailyCheckinsRollupComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-daily-checkins-rollup/weekly-daily-checkins-rollup.component';
import {WeeklyChapterListComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-chapter-list.component';
import {WeeklyMealPlanCheckComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-meal-plan-check.component';
import {WeeklyHowItsGoingComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-how-its-going/weekly-how-its-going.component';
import {WeeklyObservationsComponent} from '@cdss/pages/patient-overview-v2/detail/weekly/components/weekly-observations/weekly-observations.component';
import {DailyBehaviourPanelComponent} from '@cdss/pages/patient-overview-v2/detail/daily/components/daily-behaviour-panel.component';
import {PatientDetailHeaderComponent} from '@cdss/pages/patient-overview-v2/components/patient-detail-header/patient-detail-header.component';
import {
    DailyPatientCarerToggleComponent,
    DailyPatientCarerToggleValue
} from '@cdss/pages/patient-overview-v2/detail/daily/components/daily-patient-carer-toggle.component';
import {Patient} from '@shared/api/models/query/patient.model';
import {
    emptyWeeklyAdherence,
    emptyWeeklyBehaviours,
    isEmptyWeeklySnapshot
} from '@cdss/pages/patient-overview-v2/detail/detail-no-data-placeholder';

@Component({
    selector: 'cdss-carer-weekly-detail-page',
    standalone: true,
    imports: [
        CommonModule,
        OverlaySpinnerComponent,
        WeeklyAdherenceDonutsComponent,
        WeeklyDailyCheckinsRollupComponent,
        WeeklyChapterListComponent,
        WeeklyMealPlanCheckComponent,
        WeeklyHowItsGoingComponent,
        WeeklyObservationsComponent,
        DailyBehaviourPanelComponent,
        PatientDetailHeaderComponent,
        DailyPatientCarerToggleComponent,
    ],
    templateUrl: './carer-weekly-detail.page.html',
    styleUrl: './carer-weekly-detail.page.scss'
})
export class CarerWeeklyDetailPage implements OnInit, OnDestroy {

    loading = true;
    patientId!: number;
    dateStart!: string;
    weekNumber: number | null = null;
    snapshot: CarerWeeklySnapshotV2 | null = null;
    noData = false;
    patient: Patient | null = null;

    private querySub: Subscription | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private patientStatisticsService: PatientStatisticsService,
        private toastr: ToastrService,
    ) {
    }

    ngOnInit(): void {
        this.patientId = Number(this.route.snapshot.params['id']);

        this.querySub = this.route.queryParams.subscribe(async (params) => {
            const dateStart = params['dateStart'] ?? null;
            if (!dateStart) {
                this.toastr.error('Missing dateStart parameter');
                this.loading = false;
                return;
            }
            this.dateStart = dateStart;
            this.weekNumber = params['weekNumber'] ? Number(params['weekNumber']) : null;
            this.loading = true;
            this.snapshot = null;
            this.noData = false;
            await this.loadSnapshot();
        });
    }

    ngOnDestroy(): void {
        this.querySub?.unsubscribe();
    }

    private async loadSnapshot(): Promise<void> {
        try {
            const response = await this.patientStatisticsService.getWeeklyDetail(
                this.patientId,
                this.dateStart,
                'carer'
            );
            // UNIVAPLAT-3042: any payload that isn't the v2 shape — the explicit
            // WeeklyNoSnapshot marker, or a pre-regeneration row with no payloadVersion
            // — is treated identically as "no v2 data". The legacy shape is no longer
            // typed or parsed. UNIVAPLAT-3259: a v2 snapshot with nothing reported or
            // completed is also "no data", so it renders exactly like a missing one.
            const hasData = isWeeklySnapshotV2(response) && !isEmptyWeeklySnapshot(response);
            this.noData = !hasData;
            this.snapshot = hasData ? response : null;
        } catch (error: any) {
            this.toastr.error(error?.error?.message ?? 'Failed to load weekly snapshot');
        } finally {
            this.loading = false;
        }
    }

    get weekEndDate(): string {
        return dayjs(this.dateStart).add(6, 'day').format('YYYY-MM-DD');
    }

    get weekLabel(): string {
        if (this.weekNumber !== null) {
            return `Week ${this.weekNumber}`;
        }
        return 'Weekly';
    }

    get weekRangeLabel(): string {
        if (!this.dateStart) return '';
        const start = dayjs(this.dateStart);
        const end = start.add(6, 'day');
        const sameMonth = start.month() === end.month();
        if (sameMonth) {
            return `${start.format('D')}–${end.format('D MMMM')}`;
        }
        return `${start.format('D MMM')} – ${end.format('D MMM')}`;
    }

    get isCurrentWeek(): boolean {
        const startOfCurrentWeek = dayjs().startOf('week');
        return dayjs(this.dateStart).isSame(startOfCurrentWeek, 'day') ||
            dayjs(this.dateStart).isAfter(startOfCurrentWeek, 'day');
    }

    onPatientLoaded(patient: Patient): void {
        this.patient = patient;
    }

    /** Only shown when both a patient-side record and a carer record exist — otherwise
     *  there is nothing to switch to. Mirrors the Daily Summary page's toggle-visibility rule. */
    get showPatientCarerToggle(): boolean {
        return !!this.patient?.user?.phoneNumber && !!this.patient?.carer;
    }

    onPatientCarerToggleSelect(value: DailyPatientCarerToggleValue): void {
        if (value === 'patient') {
            this.router.navigate(
                ['/cdss/patient-overview', this.patientId, 'patient-weekly'],
                {queryParams: {dateStart: this.dateStart, ...(this.weekNumber !== null ? {weekNumber: this.weekNumber} : {})}}
            );
        }
    }

    /** Adherence for the donuts; zeroed when the week has no data (UNIVAPLAT-3259). */
    get adherence(): CarerWeeklyAdherence {
        return this.snapshot?.adherence ?? emptyWeeklyAdherence('carer');
    }

    /** Chapters for the list; empty (its own "No chapters" state) when the week has no data. */
    get chapters(): WeeklySnapshot_Chapter[] {
        return this.snapshot?.chapters ?? [];
    }

    get chapterAdherencePct(): number {
        const a = this.snapshot?.adherence?.chapters;
        if (!a || a.scheduled === 0) return 0;
        return Math.round(a.completed * 100 / a.scheduled);
    }

    get checkInAdherencePct(): number {
        const a = this.snapshot?.adherence?.checkIns;
        if (!a || a.scheduled === 0) return 0;
        return Math.round(a.completed * 100 / a.scheduled);
    }

    /**
     * ARFID signal for the carer copy variants (UNIVAPLAT-3053 `careProgramScope`,
     * UNIVAPLAT-2962 OQ-FE2). `snapshot` is only ever the v2 shape (UNIVAPLAT-3042) —
     * a pre-regeneration/no-data week always falls back to the standard copy since
     * `snapshot` itself is `null` there.
     */
    get isArfid(): boolean {
        return this.snapshot?.careProgramScope === 'ARFID';
    }

    /** Meal plan panel title (UNIVAPLAT-3037) — "Meal plan" or "This week's eating". */
    get mealPlanTitle(): string {
        return resolveWeeklyCarerCopy(this.isArfid).mealPlanTitle;
    }

    /**
     * Meal plan panel data (UNIVAPLAT-3037) — the v2 payload's `mealPlan` key, itself
     * possibly the v2 `{items, somethingElse, nothingThisWeek}` shape or a legacy
     * string[]-per-side week (pre-2.9.0 app, an axis orthogonal to `payloadVersion` —
     * see `WeeklyMealPlanLegacy`). `cdss-weekly-meal-plan-check` renders both shapes
     * and the null/no-answer case — never gated behind an `*ngIf` here.
     */
    get mealPlanData(): WeeklyMealPlanV2 | WeeklyMealPlanLegacy | null {
        return this.snapshot?.mealPlan ?? null;
    }

    /** "How it's going" panel data (UNIVAPLAT-3038), v2-only — see `mealPlanData` above. */
    get howItsGoingData(): WeeklyHowItsGoingV2 | null {
        return this.snapshot?.howItsGoing ?? null;
    }

    /** "Your observations" panel data (UNIVAPLAT-3038), v2-only — see `mealPlanData` above. */
    get observationsData(): WeeklyObservationsV2 | null {
        return this.snapshot?.observations ?? null;
    }

    /** Per-area labels for the observations panel, ARFID-aware (UNIVAPLAT-3038, UNIVAPLAT-2962 OQ-FE2). */
    get observationLabels(): WeeklyObservationLabels {
        return resolveWeeklyCarerCopy(this.isArfid).observationLabels;
    }

    /** Answer labels for the "How it's going" panel — identical copy across
     *  ARFID/standard variants, resolved the same way as the other carer copy. */
    get howItsGoingAnswerLabels(): WeeklyHowItsGoingAnswerLabels {
        return resolveWeeklyCarerCopy(this.isArfid).howItsGoingAnswerLabels;
    }

    /**
     * "Daily check-ins this week" panel data (UNIVAPLAT-3039) — the v2 payload's
     * `dailyCheckins` roll-up, identical shape to the patient side (UNIVAPLAT-2962
     * OQ-FE1, answered 2026-09-29, backend UNIVAPLAT-3146). v2-only — see
     * `mealPlanData` above for the no-data/pre-regeneration case.
     */
    get dailyCheckinsRollup(): WeeklyDailyCheckinsRollupV2 | null {
        return this.snapshot?.dailyCheckins ?? null;
    }

    /**
     * "Observed behaviours" panel data, mapped onto the exact same `DailyBehaviourV2[]`
     * shape and `cdss-daily-behaviour-panel` component the Daily Summary page uses, so
     * the two pages render identically — see the patient page's identical getter
     * (`patientBehaviourLog`) for the full rationale on the `.carer` side selection and
     * the real backend `percentage` (UNIVAPLAT-3037 fix-up). Supersedes the retired
     * whole-week behaviour-log field and its `cdss-weekly-behaviour-log` rendering
     * (removed in UNIVAPLAT-3042).
     */
    get carerBehaviourLog(): DailyBehaviourV2[] | null {
        const list = this.snapshot?.observedBehaviours;
        if (!list) return this.noData ? emptyWeeklyBehaviours('carer') : null;
        return list.map(behaviour => ({
            name: behaviour.name,
            counter: behaviour.carer.value,
            severity: behaviour.carer.severity,
            trend: behaviour.carer.trend,
            percentage: behaviour.carer.percentage,
        }));
    }

    navigateToPrevWeek(): void {
        const prevWeekStart = dayjs(this.dateStart).subtract(7, 'day').format('YYYY-MM-DD');
        const weekNumber = this.weekNumber !== null ? this.weekNumber - 1 : null;
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId, 'carer-weekly'],
            {queryParams: {dateStart: prevWeekStart, ...(weekNumber !== null ? {weekNumber} : {})}}
        );
    }

    navigateToNextWeek(): void {
        if (this.isCurrentWeek) return;
        const nextWeekStart = dayjs(this.dateStart).add(7, 'day').format('YYYY-MM-DD');
        const weekNumber = this.weekNumber !== null ? this.weekNumber + 1 : null;
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId, 'carer-weekly'],
            {queryParams: {dateStart: nextWeekStart, ...(weekNumber !== null ? {weekNumber} : {})}}
        );
    }

    /** Mirrors the Daily Summary page's Back behaviour (UNIVAPLAT-2360 follow-up): return to the
     *  Overview page on the correct patient/carer tab, opened on the week currently displayed
     *  here rather than always falling back to the care programme's current week. */
    goBack(): void {
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId],
            {queryParams: {type: 'carer', dateStart: this.dateStart}}
        );
    }
}
